import { DatePicker, message } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import "./EmployeeSpecsFilterBar.scss";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Reusable period filter strip.
 *
 * variant:
 *  - "full"  → year + month pills + from/to (Oraret)
 *  - "month" → year + month pills (Paga)
 *  - "year"  → multi-select year pills with data only (Lejet)
 */
const EmployeeSpecsFilterBar = ({
  variant = "full",
  year,
  selectedMonth,
  selectedYears = [],
  yearsWithData = [],
  fromDate,
  toDate,
  monthsWithData,
  loading = false,
  onYearChange,
  onMonthSelect,
  onYearToggle,
  onRangeApply,
}) => {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [draftFrom, setDraftFrom] = useState(fromDate);
  const [draftTo, setDraftTo] = useState(toDate);

  const showMonths = variant === "full" || variant === "month";
  const showRange = variant === "full";
  const showYearPills = variant === "year";
  const showYearNav = variant !== "year";

  useEffect(() => {
    setDraftFrom(fromDate);
    setDraftTo(toDate);
  }, [fromDate, toDate]);

  const hasData = (m) =>
    monthsWithData instanceof Set
      ? monthsWithData.has(m)
      : Array.isArray(monthsWithData)
        ? monthsWithData.includes(m)
        : false;

  const handleApply = () => {
    if (!draftFrom || !draftTo) {
      message.error("Ju lutem zgjidhni datën e fillimit dhe mbarimit.");
      return;
    }
    const ok = onRangeApply?.(draftFrom, draftTo);
    if (!ok) {
      message.error("Data e fillimit duhet të jetë para datës së mbarimit.");
    }
  };

  return (
    <div
      className={`employee-specs-filter-bar esfb-variant-${variant} ${
        isDarkMode ? "dark" : "light"
      }`}
    >
      {showYearNav ? (
        <div className="esfb-year">
          <button
            type="button"
            className="esfb-chevron"
            disabled={loading}
            aria-label="Viti i kaluar"
            onClick={() => onYearChange?.(year - 1)}
          >
            <LeftOutlined />
          </button>
          <span className="esfb-year-label">{year}</span>
          <button
            type="button"
            className="esfb-chevron"
            disabled={loading}
            aria-label="Viti i ardhshëm"
            onClick={() => onYearChange?.(year + 1)}
          >
            <RightOutlined />
          </button>
        </div>
      ) : null}

      {showYearPills ? (
        <div className="esfb-years" role="listbox" aria-label="Vitet">
          {yearsWithData.length === 0 ? (
            <span className="esfb-years-empty">
              {loading ? "Duke ngarkuar vitet…" : "Nuk ka vite me leje"}
            </span>
          ) : (
            yearsWithData.map((y) => {
              const selected = selectedYears.includes(y);
              return (
                <button
                  key={y}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`esfb-year-pill ${selected ? "is-selected" : "has-data"}`}
                  disabled={loading}
                  onClick={() => onYearToggle?.(y)}
                >
                  {y}
                  <span className="esfb-dot" aria-hidden />
                </button>
              );
            })
          )}
        </div>
      ) : null}

      {showMonths ? (
        <>
          <div className="esfb-divider" />
          <div className="esfb-months" role="listbox" aria-label="Muajt">
            {MONTHS.map((label, idx) => {
              const m = idx + 1;
              const selected = selectedMonth === m;
              const withData = hasData(m);
              const className = [
                "esfb-month",
                selected ? "is-selected" : "",
                !selected && withData ? "has-data" : "",
                !selected && !withData ? "is-empty" : "",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  key={label}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={className}
                  disabled={loading}
                  onClick={() => onMonthSelect?.(m)}
                >
                  {label}
                  {withData ? <span className="esfb-dot" aria-hidden /> : null}
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {showRange ? (
        <>
          <div className="esfb-divider" />
          <div className="esfb-range">
            <DatePicker
              placeholder="Nga data"
              value={draftFrom}
              allowClear={false}
              disabled={loading}
              format="DD/MM/YYYY"
              classNames={{
                popup: { root: isDarkMode ? "darkDropdown" : "" },
              }}
              onChange={(val) => setDraftFrom(val)}
            />
            <span className="esfb-range-sep">–</span>
            <DatePicker
              placeholder="Te data"
              value={draftTo}
              allowClear={false}
              disabled={loading}
              format="DD/MM/YYYY"
              classNames={{
                popup: { root: isDarkMode ? "darkDropdown" : "" },
              }}
              onChange={(val) => setDraftTo(val)}
            />
            <MondayButton
              className="mondayButtonGreen"
              disabled={loading}
              onClick={handleApply}
            >
              Apliko
            </MondayButton>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default EmployeeSpecsFilterBar;
